import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans } from "@/hooks/use-plans";
import { useCurrentUsage } from "@/hooks/use-current-usage";
import { SYSTEM_FIELD_CONFIG } from "@/lib/plans/enforce";
import { Truck } from "lucide-react";
import { WidgetProps } from "@/lib/widgets/types";

export function FleetUsageWidget({ config }: WidgetProps) {
  const { activeCompany } = useAuth();
  const { data: plans } = useCompanyPlans(activeCompany?.id);
  const { data: usage, isLoading } = useCurrentUsage(activeCompany?.id || '', 'fleet');

  const currentPlan = plans?.find(p => p.system?.code === 'fleet');
  const limits = currentPlan?.plan?.features?.limits || {};
  
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-sm text-muted-foreground">Carregando uso...</div>
        </CardContent>
      </Card>
    );
  }

  const getUsagePercentage = (current: number, limit: number) => {
    if (limit === 999999) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getVariant = (percentage: number): "default" | "destructive" | "secondary" => {
    if (percentage > 90) return "destructive";
    if (percentage > 70) return "secondary";
    return "default";
  };

  const vehicleLimit = limits.fleet_vehicles_max || limits.vehicles_max || 0;
  const vehicleUsage = usage?.fleet_vehicles_max || usage?.vehicles || 0;
  const vehiclePercentage = getUsagePercentage(vehicleUsage, vehicleLimit);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Uso FleetONE
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {vehicleLimit === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhum plano ativo</div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Veículos</span>
              <Badge variant={getVariant(vehiclePercentage)}>
                {vehicleUsage} / {vehicleLimit === 999999 ? '∞' : vehicleLimit}
              </Badge>
            </div>
            <Progress value={vehiclePercentage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
